import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import AIProcessing from '../components/AIProcessing';
import AIComplete from '../components/AIComplete';
import { useCopyContext } from '../context/CopyContext';

type GeneratedResult = {
  caption?: string;
  hashtags?: string[];
  cta?: string;
  best_time?: string;
  tips?: string[];
};

const platforms = [
  { name: 'Instagram', icon: '📸', color: '#E1306C' },
  { name: 'Facebook', icon: '📘', color: '#1877F2' },
  { name: 'WhatsApp', icon: '💬', color: '#25D366' },
  { name: 'LinkedIn', icon: '💼', color: '#0A66C2' },
  { name: 'YouTube', icon: '▶️', color: '#FF0000' },
  { name: 'Twitter', icon: '🐦', color: '#1D9BF0' },
  { name: 'Google', icon: '🔍', color: '#4285F4' },
  { name: 'SMS', icon: '📱', color: '#9333EA' },
];

const contentTypes = [
  'Regular Post',
  'Festive Post',
  'Offer/Discount',
  'Product Launch',
  'Testimonial',
  'Educational',
  'Behind the Scenes',
];

const tones = ['Professional', 'Friendly', 'Humorous', 'Inspirational', 'Urgent', 'Educational'];
const languages = ['Hinglish 🇮🇳', 'Hindi', 'English'];

export function ContentGeneratorPage() {
  const { copyText } = useCopyContext();
  const [mode, setMode] = useState<'post' | 'carousel'>('post');
  const [platform, setPlatform] = useState('Instagram');
  const [contentType, setContentType] = useState('Regular Post');
  const [topic, setTopic] = useState('');
  const [tone, setTone] = useState('Friendly');
  const [language, setLanguage] = useState('Hinglish');
  const [slideCount, setSlideCount] = useState(5);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<GeneratedResult | null>(null);
  const [carousel, setCarousel] = useState<any>(null);
  const [error, setError] = useState('');
  const [showComplete, setShowComplete] = useState(false);

  const selectedPlatform = platforms.find((p) => p.name === platform);

  async function generatePost() {
    setGenerating(true);
    setError('');
    setResult(null);
    try {
      const r = await api.post('/content-gen/generate', {
        platform,
        contentType,
        topic,
        tone: tone.replace(' 🇮🇳', ''),
        language: language.replace(' 🇮🇳', ''),
      });
      setResult(r.data.content);
      setShowComplete(true);
    } catch {
      setError('Generation failed. Try again.');
    } finally {
      setGenerating(false);
    }
  }

  async function generateCarousel() {
    setGenerating(true);
    setError('');
    setResult(null);
    setCarousel(null);
    try {
      const r = await api.post('/content-gen/carousel', {
        topic,
        platform,
        tone,
        language: language.replace(' 🇮🇳', ''),
        slideCount,
      });
      setCarousel(r.data.carousel);
      setShowComplete(true);
    } catch {
      setError('Generation failed. Try again.');
    } finally {
      setGenerating(false);
    }
  }

  async function generateContent() {
    if (mode === 'post') {
      await generatePost();
      return;
    }
    await generateCarousel();
  }

  function handleGenerate() {
    if (generating || !topic.trim()) return;
    void generateContent();
  }
  const handleGenerateRef = useRef(handleGenerate);
  handleGenerateRef.current = handleGenerate;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey && !generating && topic.trim()) {
        const target = e.target as HTMLElement;
        if (target.tagName === 'TEXTAREA') return;
        handleGenerateRef.current();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [generating, topic]);

  async function copyCaption() {
    if (!result?.caption) return;
    copyText(result.caption, 'Copied!');
  }

  async function copyHashtags() {
    const tags = (result?.hashtags || []).map((h) => `#${h}`).join(' ');
    if (!tags) return;
    copyText(tags, 'Copied!');
  }

  async function copyFullPost() {
    const caption = result?.caption || '';
    const hashtags = (result?.hashtags || []).map((h) => `#${h}`).join(' ');
    const full = `${caption}\n\n${hashtags}`.trim();
    if (!full) return;
    copyText(full, 'Copied!');
  }

  async function copySlideCaption(caption?: string) {
    if (!caption) return;
    copyText(caption, 'Copied!');
  }

  async function copyPrompt(prompt?: string) {
    if (!prompt) return;
    copyText(prompt, 'Copied!');
  }

  async function copyCarouselCaption() {
    if (!carousel?.overall_caption) return;
    copyText(carousel.overall_caption, 'Copied!');
  }

  async function copyCarouselHashtags() {
    const tags = (carousel?.hashtags || []).map((h: string) => `#${h}`).join(' ');
    if (!tags) return;
    copyText(tags, 'Copied!');
  }

  async function copyEverything() {
    const slideCaptions = (carousel?.slides || [])
      .map((s: any, i: number) => `Slide ${i + 1}: ${s.caption || ''}`)
      .join('\n\n');
    const hashtags = (carousel?.hashtags || []).map((h: string) => `#${h}`).join(' ');
    const full = `${carousel?.overall_caption || ''}\n\n${slideCaptions}\n\n${hashtags}`.trim();
    if (!full) return;
    copyText(full, 'Copied!');
  }

  return (
    <div className="odc-page min-w-0 max-w-full space-y-6 px-0 sm:px-0">
      <header>
        <span
          className="inline-flex rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider"
          style={{ background: 'rgba(16,185,129,0.2)', color: '#10B981' }}
        >
          AI Content Generator
        </span>
        <h1 className="mt-3 text-3xl font-bold" style={{ color: 'var(--text)' }}>
          Generate Social Media Posts with AI
        </h1>
        <p className="mt-2 text-sm" style={{ color: 'var(--muted)' }}>
          Enter your topic — ODC AI will craft the perfect post
        </p>
      </header>

      <section className="odc-card mx-auto w-full max-w-[700px] !p-6">
        <div className="space-y-5">
          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
              Mode
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                className="rounded-xl border px-4 py-3 text-sm font-bold transition"
                style={
                  mode === 'post'
                    ? {
                        color: '#000',
                        borderColor: '#BE5103',
                        background: 'linear-gradient(135deg, #BE5103, #B7410E)',
                      }
                    : { color: 'var(--text)', borderColor: 'var(--border)', background: 'var(--card)' }
                }
                onClick={() => {
                  setMode('post');
                  setCarousel(null);
                }}
              >
                📝 Single Post
              </button>
              <button
                type="button"
                className="rounded-xl border px-4 py-3 text-sm font-bold transition"
                style={
                  mode === 'carousel'
                    ? {
                        color: '#000',
                        borderColor: '#BE5103',
                        background: 'linear-gradient(135deg, #BE5103, #B7410E)',
                      }
                    : { color: 'var(--text)', borderColor: 'var(--border)', background: 'var(--card)' }
                }
                onClick={() => {
                  setMode('carousel');
                  setResult(null);
                }}
              >
                🎠 Carousel Post
              </button>
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
              Platform
            </p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {platforms.map((p) => {
                const active = platform === p.name;
                return (
                  <button
                    key={p.name}
                    type="button"
                    className="rounded-xl border px-3 py-2 text-sm font-semibold transition"
                    style={{
                      borderColor: active ? p.color : 'var(--border)',
                      background: active ? `${p.color}1A` : 'var(--card)',
                      color: 'var(--text)',
                    }}
                    onClick={() => setPlatform(p.name)}
                  >
                    {p.icon} {p.name}
                  </button>
                );
              })}
            </div>
          </div>

          {mode === 'post' ? (
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                Content Type
              </p>
              <div className="flex flex-wrap gap-2">
                {contentTypes.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className="odc-pill-tab"
                    style={
                      contentType === c
                        ? {
                            borderColor: '#BE5103',
                            background: 'rgba(190,81,3,0.16)',
                            color: 'var(--text)',
                          }
                        : undefined
                    }
                    onClick={() => setContentType(c)}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {mode === 'carousel' ? (
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                Number of Slides
              </p>
              <div className="flex flex-wrap gap-2">
                {[3, 4, 5, 6, 7].map((count) => (
                  <button
                    key={count}
                    type="button"
                    className="odc-pill-tab"
                    style={
                      slideCount === count
                        ? {
                            borderColor: '#BE5103',
                            background: 'rgba(190,81,3,0.16)',
                            color: 'var(--text)',
                          }
                        : undefined
                    }
                    onClick={() => setSlideCount(count)}
                  >
                    {count}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
              Topic
            </p>
            <input
              className="odc-input"
              placeholder="e.g. Diwali sale 50% off, New property launch, Restaurant special menu..."
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !generating && topic.trim()) {
                  e.preventDefault();
                  e.stopPropagation();
                  handleGenerate();
                }
              }}
            />
            <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
              Press Enter to continue
            </p>
          </div>

          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
              Tone
            </p>
            <div className="flex flex-wrap gap-2">
              {tones.map((t) => (
                <button
                  key={t}
                  type="button"
                  className="odc-pill-tab"
                  style={
                    tone === t
                      ? {
                          borderColor: '#BE5103',
                          background: 'rgba(190,81,3,0.16)',
                          color: 'var(--text)',
                        }
                      : undefined
                  }
                  onClick={() => setTone(t)}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
              Language
            </p>
            <div className="flex flex-wrap gap-2">
              {languages.map((l) => (
                <button
                  key={l}
                  type="button"
                  className="odc-pill-tab"
                  style={
                    language.replace(' 🇮🇳', '') === l.replace(' 🇮🇳', '')
                      ? {
                          borderColor: '#10B981',
                          background: 'rgba(16,185,129,0.16)',
                          color: 'var(--text)',
                        }
                      : undefined
                  }
                  onClick={() => setLanguage(l)}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>

          {error ? (
            <p className="text-sm font-medium" style={{ color: '#EF4444' }}>
              {error}
            </p>
          ) : null}

          <button
            type="button"
            className="w-full rounded-xl px-5 py-3 text-base font-semibold text-black disabled:cursor-not-allowed disabled:opacity-60"
            style={{ background: 'linear-gradient(135deg, #BE5103, #B7410E)' }}
            onClick={handleGenerate}
            disabled={generating || !topic.trim()}
          >
            {generating ? '✨ ODC AI is crafting your content...' : mode === 'post' ? '✨ Generate Post' : '✨ Generate Carousel'}
          </button>
        </div>
      </section>

      {result ? (
        <section
          className="odc-card mx-auto w-full max-w-[900px] space-y-4 !p-6"
          style={{ borderColor: 'rgba(16,185,129,0.4)' }}
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span
              className="rounded-full px-3 py-1 text-xs font-bold"
              style={{
                background: `${selectedPlatform?.color || '#10B981'}22`,
                color: selectedPlatform?.color || '#10B981',
              }}
            >
              {selectedPlatform?.icon} {platform}
            </span>
            <span
              className="rounded-full px-3 py-1 text-xs font-bold"
              style={{ background: 'rgba(16,185,129,0.15)', color: '#10B981' }}
            >
              Generated by ODC AI
            </span>
          </div>

          <div>
            <p className="mb-2 text-sm font-bold" style={{ color: 'var(--text)' }}>
              📝 Post Caption
            </p>
            <textarea className="odc-input w-full" rows={6} readOnly value={result.caption || ''} />
            <button type="button" className="odc-btn-ghost mt-2 !text-xs" onClick={() => void copyCaption()}>
              📋 Copy
            </button>
          </div>

          <div>
            <div className="mb-2 flex flex-wrap items-center gap-3">
              <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>
                # Hashtags
              </p>
              <button type="button" className="odc-btn-ghost !py-1.5 !text-xs" onClick={() => void copyHashtags()}>
                Copy All Hashtags
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {(result.hashtags || []).map((h) => (
                <span
                  key={h}
                  className="rounded-full px-2.5 py-1 text-xs font-semibold"
                  style={{ background: 'rgba(16,185,129,0.18)', color: '#10B981' }}
                >
                  #{h}
                </span>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-2 text-sm font-bold" style={{ color: 'var(--text)' }}>
              🎯 Call to Action
            </p>
            <p style={{ color: 'var(--muted)' }}>{result.cta || '-'}</p>
          </div>

          <div className="flex flex-wrap items-center gap-4 text-sm" style={{ color: 'var(--muted)' }}>
            <span>⏰ Best time: {result.best_time || '-'}</span>
            <span>Platform: {platform}</span>
          </div>

          <div>
            <p className="mb-2 text-sm font-bold" style={{ color: 'var(--text)' }}>
              💡 Pro Tips
            </p>
            <div className="space-y-1 text-sm" style={{ color: 'var(--muted)' }}>
              {(result.tips || []).map((t, i) => (
                <p key={`${t}-${i}`}>• {t}</p>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="odc-btn-ghost"
              onClick={() => {
                setResult(null);
                setTopic('');
              }}
            >
              🔄 Generate Another
            </button>
            <Link to="/calendar" className="odc-btn-ghost">
              📅 Schedule Post
            </Link>
            <button type="button" className="odc-btn-primary" onClick={() => void copyFullPost()}>
              📋 Copy Full Post
            </button>
          </div>
        </section>
      ) : null}

      {carousel ? (
        <section className="odc-card mx-auto w-full max-w-[1100px] space-y-4 !p-6" style={{ borderColor: 'rgba(16,185,129,0.4)' }}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <p className="text-lg font-extrabold" style={{ color: '#10B981' }}>
                🎠 Carousel Ready!
              </p>
              <span
                className="rounded-full px-3 py-1 text-xs font-bold"
                style={{
                  background: `${selectedPlatform?.color || '#10B981'}22`,
                  color: selectedPlatform?.color || '#10B981',
                }}
              >
                {selectedPlatform?.icon} {platform}
              </span>
            </div>
          </div>
          <h3 className="text-xl font-bold" style={{ color: 'var(--text)' }}>
            {carousel.carousel_title}
          </h3>

          <div>
            <p className="mb-3 text-sm font-bold" style={{ color: 'var(--text)' }}>
              📱 Slides Content
            </p>
            {(carousel.slides || []).map((slide: any, idx: number) => (
              <div key={`${slide.slide_number || idx}-${slide.headline || 'slide'}`} className="odc-card mb-3 !p-4" style={{ transform: 'none' }}>
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <span
                    className="rounded-full px-2.5 py-1 text-xs font-bold"
                    style={{ background: 'rgba(190,81,3,0.2)', color: '#BE5103' }}
                  >
                    Slide {slide.slide_number || idx + 1}
                  </span>
                  <span
                    className="rounded-full px-2.5 py-1 text-xs font-bold"
                    style={{ background: 'rgba(16,185,129,0.18)', color: '#10B981' }}
                  >
                    {slide.slide_type || 'Content'}
                  </span>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-3">
                    <p className="text-lg font-extrabold" style={{ color: slide?.design?.accent_color || '#BE5103' }}>
                      {slide.headline}
                    </p>
                    <p className="text-sm" style={{ color: 'var(--muted)' }}>
                      {slide.subtext}
                    </p>
                    <textarea className="odc-input w-full" rows={3} readOnly value={slide.caption || ''} />
                    <button type="button" className="odc-btn-ghost !py-1.5 !text-xs" onClick={() => void copySlideCaption(slide.caption)}>
                      Copy Slide Caption
                    </button>
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>
                      🎨 Design Guide
                    </p>
                    <div className="rounded-lg border p-2 text-xs" style={{ borderColor: 'var(--border)' }}>
                      <strong>Background:</strong> {slide?.design?.background || '-'}
                    </div>
                    <div className="rounded-lg border p-2 text-xs" style={{ borderColor: 'var(--border)' }}>
                      <strong>Text Color:</strong> {slide?.design?.text_color || '-'}
                    </div>
                    <div className="rounded-lg border p-2 text-xs" style={{ borderColor: 'var(--border)' }}>
                      <strong>Elements:</strong> {(slide?.design?.elements || []).join(', ') || '-'}
                    </div>
                    <div className="rounded-lg border p-2 text-xs" style={{ borderColor: 'var(--border)' }}>
                      <strong>Layout:</strong> {slide?.design?.layout || '-'}
                    </div>
                  </div>
                </div>

                <div className="mt-3">
                  <p className="mb-2 text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                    🤖 AI Image Prompt (for DALL-E/Midjourney/Canva AI)
                  </p>
                  <input className="odc-input" readOnly value={slide.image_prompt || ''} />
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <button type="button" className="odc-btn-ghost !py-1.5 !text-xs" onClick={() => void copyPrompt(slide.image_prompt)}>
                      Copy Prompt
                    </button>
                    <a
                      href={`https://www.canva.com/search/templates?q=${encodeURIComponent(slide.canva_suggestion || '')}`}
                      target="_blank"
                      rel="noreferrer"
                      className="odc-btn-ghost !py-1.5 !text-xs"
                    >
                      🎨 Canva: Search '{slide.canva_suggestion || ''}'
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="odc-card !p-4" style={{ transform: 'none' }}>
            <div className="mb-2 flex flex-wrap items-center gap-3">
              <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>
                📝 Overall Post Caption
              </p>
              <button type="button" className="odc-btn-ghost !py-1.5 !text-xs" onClick={() => void copyCarouselCaption()}>
                Copy Caption
              </button>
            </div>
            <textarea className="odc-input w-full" rows={4} readOnly value={carousel.overall_caption || ''} />
          </div>

          <div>
            <div className="mb-2 flex flex-wrap items-center gap-3">
              <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>
                # Hashtags
              </p>
              <button type="button" className="odc-btn-ghost !py-1.5 !text-xs" onClick={() => void copyCarouselHashtags()}>
                Copy All
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {(carousel.hashtags || []).map((h: string) => (
                <span key={h} className="rounded-full px-2.5 py-1 text-xs font-semibold" style={{ background: 'rgba(16,185,129,0.18)', color: '#10B981' }}>
                  #{h}
                </span>
              ))}
            </div>
          </div>

          <div className="odc-card !p-4" style={{ transform: 'none' }}>
            <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>
              🎵 Background Music Suggestions
            </p>
            <p className="mt-1 text-xs" style={{ color: 'var(--muted)' }}>
              Perfect music for your Reels/Shorts
            </p>
            <div className="mt-3">
              {(carousel.music_suggestions || []).map((music: any, i: number) => (
                <div
                  key={`${music.song || 'song'}-${i}`}
                  className="flex flex-wrap items-center justify-between gap-2 border-b py-2 text-sm last:border-b-0"
                  style={{ borderColor: 'var(--border)' }}
                >
                  <div>
                    <p className="font-bold" style={{ color: 'var(--text)' }}>
                      🎵 {music.song}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--muted)' }}>
                      {music.artist}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{ background: 'rgba(245,158,11,0.2)', color: '#F59E0B' }}>
                      {music.mood}
                    </span>
                    <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{ background: 'rgba(59,130,246,0.2)', color: '#3B82F6' }}>
                      {music.platform}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>
              💡 Posting Tips
            </p>
            <div className="mt-2 space-y-1 text-sm" style={{ color: 'var(--muted)' }}>
              {(carousel.posting_tips || []).map((t: string, i: number) => (
                <p key={`${t}-${i}`}>✅ {t}</p>
              ))}
              <p>⏰ Best time: {carousel.best_time || '-'}</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button type="button" className="odc-btn-ghost" onClick={() => setCarousel(null)}>
              🔄 Generate New Carousel
            </button>
            <Link to="/calendar" className="odc-btn-ghost">
              📅 Schedule
            </Link>
            <button type="button" className="odc-btn-primary" onClick={() => void copyEverything()}>
              📋 Copy Everything
            </button>
          </div>
        </section>
      ) : null}
      <AIProcessing isVisible={generating} agentName="Content Generator" />
      <AIComplete
        isVisible={showComplete}
        agentName="Content Generator"
        message="Your content is ready! Copy and publish it now 🚀"
        onClose={() => setShowComplete(false)}
      />
    </div>
  );
}
